import React, { useState, useEffect, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useSidebar } from '../../contexts/SidebarContext';
import HeaderNavigation from '../../components/ui/HeaderNavigation';
import MainSidebar from '../../components/ui/MainSidebar';
import BreadcrumbNavigation from '../../components/ui/BreadcrumbNavigation';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';
import Input from '../../components/ui/Input';
import RecruitmentFunnel from './components/RecruitmentFunnel';
import ApplicantFilters from './components/ApplicantFilters';
import ApplicantTable from './components/ApplicantTable';
import ApplicantDetailModal from './components/ApplicantDetailModal';
import AddApplicantModal from './components/AddApplicantModal';
import ImportApplicantsModal from './components/ImportApplicantsModal';
import DuplicateApplicationsView from './components/DuplicateApplicationsView';
import jobApplicationService from '../../services/jobApplicationService';
import OfferLetters from '../hr/OfferLetters';
import ProbationLetters from '../hr/ProbationLetters';

const ApplicantTrackingSystem = () => {
  const { user, userProfile } = useAuth();
  const { isCollapsed } = useSidebar();
  const location = useLocation();
  const navigate = useNavigate();

  const getTabFromPath = (path) => {
    if (path.includes('duplicates')) return 'duplicates';
    if (path.includes('offer-letters')) return 'offer-letters';
    if (path.includes('probation-letters')) return 'probation-letters';
    return 'dashboard';
  };

  const [activeTab, setActiveTab] = useState(() => getTabFromPath(location.pathname));

  useEffect(() => {
    setActiveTab(getTabFromPath(location.pathname));
  }, [location.pathname]);

  const tabs = [
    { id: 'dashboard', label: 'ATS Dashboard', icon: 'LayoutDashboard', path: '/erp/applicant-tracking-system' },
    { id: 'duplicates', label: 'Duplicate Applications', icon: 'Copy', path: '/erp/applicant-tracking-system/duplicates' },
    { id: 'offer-letters', label: 'Offer Letters', icon: 'FileText', path: '/erp/applicant-tracking-system/offer-letters' },
    { id: 'probation-letters', label: 'Probation Letters', icon: 'Clock', path: '/erp/applicant-tracking-system/probation-letters' }
  ];

  const handleTabChange = (tab) => {
    setActiveTab(tab.id);
    navigate(tab.path);
  };

  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [selectedApplicant, setSelectedApplicant] = useState(null);
  const [selectedApplicants, setSelectedApplicants] = useState([]);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [modalInitialTab, setModalInitialTab] = useState('profile');
  const [applicants, setApplicants] = useState([]);
  const [funnelData, setFunnelData] = useState({
    applied: 0,
    screening: 0,
    internal_interview: 0,
    external_interview: 0,
    offer: 0,
    hired: 0
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({
    search: '',
    position: '',
    status: '',
    hrAssignee: '',
    is_duplicate: '',
    dateFrom: '',
    dateTo: ''
  });

  // Deep-link support from Lifecycle dashboard and other pages
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const status = params.get('status');
    if (status) {
      setFilters((prev) => (prev.status === status ? prev : { ...prev, status }));
    }
  }, [location.search]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const applicantId = params.get('applicantId');
    if (!applicantId || applicants.length === 0) return;
    const match = applicants.find((a) => a.id === applicantId);
    if (match) {
      setSelectedApplicant(match);
      setModalInitialTab('profile');
    }
  }, [location.search, applicants]);

  // Transform database application to component format
  const transformApplication = (app) => {
    return {
      id: app.id,
      name: app.applicant_name || `${app.first_name || ''} ${app.last_name || ''}`.trim() || app.applicant_name,
      email: app.email,
      phone: app.phone || '',
      position: app.position,
      experience: app.experience_level || '',
      location: app.location || '',
      applicationDate: app.applied_date || app.created_at?.split('T')[0] || '',
      created_at: app.created_at || '',
      status: app.status || app.screening_status || 'pending',
      priority: app.priority || 'medium',
      pipeline_stage: app.pipeline_stage || 'applied',
      ats_score: app.ats_score,
      ats_report: app.ats_report,
      parsed_resume_data: app.parsed_resume_data,
      stage_updated_at: app.stage_updated_at,
      is_duplicate: app.is_duplicate,
      duplicate_group_id: app.duplicate_group_id,
      duplicate_reason: app.duplicate_reason,
      ai_screening_status: app.ai_screening_status,
      ai_screening_score: app.ai_screening_score,
      ai_screening_result: app.ai_screening_result,
      ai_screening_completed_at: app.ai_screening_completed_at,
      ai_screening_evaluation: app.ai_screening_evaluation,
      hr_screening_score: app.hr_screening_score,
      hr_screening_status: app.hr_screening_status,
      hr_screening_answers: app.hr_screening_answers,
      hr_screening_completed_at: app.hr_screening_completed_at,
      hr_screening_unlocked_at: app.hr_screening_unlocked_at,
      hr_screening_deadline: app.hr_screening_deadline,
      offered_ctc: app.offered_ctc,
      offered_designation: app.offered_designation,
      joining_date: app.joining_date,
      employment_type: app.employment_type,
      work_location: app.work_location,
      notice_period: app.notice_period,
      final_hr_notes: app.final_hr_notes,
      final_hr_status: app.final_hr_status,
      avatar: `https://ui-avatars.com/api/?name=${encodeURIComponent(app.applicant_name || app.first_name || 'User')}&background=random`,
      hrAssigned: app.assigned_hr_id ? {
        name: app.assigned_hr_name || 'HR Manager',
        avatar: `https://ui-avatars.com/api/?name=${encodeURIComponent(app.assigned_hr_name || 'HR')}&background=random`
      } : null,
      skills: [],
      education: app.education_requirements || '',
      expectedSalary: app.expected_salary || 0,
      resumeFileName: app.resume_url ? app.resume_url.split('/').pop() : (app.resumeUrl ? app.resumeUrl.split('/').pop() : ''),
      resumeUrl: app.resume_url || app.resumeUrl || '',
      resume_url: app.resume_url || app.resumeUrl || '',
      interviewDate: app.last_interview_date || app.interview_date,
      interviewMode: app.interview_mode,
      interviewLocation: app.interview_location,
      interviewerName: app.interviewer_name,
      interviewScore: app.interview_score,
      interviewFeedback: app.interview_feedback,
      interviewStage: app.last_interview_stage,
      interviewResult: app.last_interview_result,
      notes: app.notes || '',
      department: app.department,
      application_source: app.application_source || 'manual',
      client_name: app.client_name
    };
  };

  // Fetch applications from API
  const loadApplications = async () => {
    try {
      setLoading(true);
      setError(null);

      // Build filter params
      const filterParams = {};
      if (filters.search) filterParams.search = filters.search;
      if (filters.position) filterParams.position = filters.position;
      if (filters.status && filters.status !== 'all') filterParams.status = filters.status;
      if (filters.is_duplicate !== '' && filters.is_duplicate !== undefined) filterParams.is_duplicate = filters.is_duplicate;
      if (filters.dateFrom) filterParams.date_from = filters.dateFrom;
      if (filters.dateTo) filterParams.date_to = filters.dateTo;

      const result = await jobApplicationService.getApplications(filterParams);

      if (result.success && result.data) {
        const transformed = result.data.map(transformApplication);
        setApplicants(transformed);

        // Compute funnel directly from the loaded applicants' status values
        // so the funnel always matches what's visible in the table
        const counts = {
          applied: 0,
          screening: 0,
          internal_interview: 0,
          external_interview: 0,
          offer: 0,
          hired: 0
        };
        transformed.forEach(app => {
          const s = (app.status || 'pending').toLowerCase();
          counts.applied++;
          if (s === 'screening')                              counts.screening++;
          if (s === 'interview' || s === 'internal_interview') counts.internal_interview++;
          if (s === 'external_interview' || s === 'external') counts.external_interview++;
          if (s === 'offer' || s === 'selected')              counts.offer++;
          if (s === 'hired' || s === 'selected' || s === 'deployed') counts.hired++;
        });
        setFunnelData(counts);
      } else {
        throw new Error(result.error || 'Failed to load applications');
      }

      // Also try to load funnel from API (for any server-side enrichment)
      // but don't overwrite the client-computed values if API fails
      try {
        const funnelResult = await jobApplicationService.getFunnel('all');
        if (funnelResult.success && funnelResult.data) {
          setFunnelData(funnelResult.data);
        }
      } catch (_) {
        // silently keep the client-computed funnel
      }
    } catch (err) {
      console.error('Error loading applications:', err);
      setError(err.message || 'Failed to load applications');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadApplications();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Reload when filters change (excluding HR assignee which is client-side)
  useEffect(() => {
    if (!loading) {
      loadApplications();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.search, filters.position, filters.status, filters.is_duplicate, filters.dateFrom, filters.dateTo]);

  // Extract unique positions from applicants for dynamic filter
  const uniquePositions = useMemo(() => {
    const positions = new Set();
    applicants.forEach(applicant => {
      if (applicant.position && applicant.position.trim()) {
        positions.add(applicant.position.trim());
      }
    });
    return Array.from(positions).sort().map(pos => ({
      value: pos,
      label: pos
    }));
  }, [applicants]);

  // Filter applicants (client-side filtering only for HR assignee since other filters are server-side)
  const filteredApplicants = applicants.filter(applicant => {
    const matchesHR = !filters.hrAssignee ||
      (applicant.hrAssigned && applicant.hrAssigned.name.toLowerCase().includes(filters.hrAssignee.toLowerCase()));
    return matchesHR;
  });

  const handleApplicantClick = (applicant) => {
    setModalInitialTab('profile');
    setSelectedApplicant(applicant);
  };

  const handleCloseModal = () => {
    setSelectedApplicant(null);
  };

  const handleCallApplicant = (applicant) => {
    // Open detail modal and switch to interactions tab for call logging
    setModalInitialTab('interactions');
    setSelectedApplicant(applicant);
  };

  const handleScheduleInterview = (applicant) => {
    // Open detail modal and switch to interviews tab for scheduling
    setModalInitialTab('interviews');
    setSelectedApplicant(applicant);
  };
const handleDeleteApplicant = async (applicant) => {
    if (!window.confirm(`Are you sure you want to delete ${applicant.name}'s application? This cannot be undone.`)) {
      return;
    }
    try {
      const result = await jobApplicationService.deleteApplication(applicant.id);
      if (result.success) {
        loadApplications(); // Refresh the list
      } else {
        alert(result.error || 'Failed to delete application');
      }
    } catch (error) {
      alert(error.message || 'Failed to delete application');
    }
  };

  const handleMoreActions = (applicant) => {
    // Open detail modal for full actions menu
    setModalInitialTab('profile');
    setSelectedApplicant(applicant);
  };

  const [showScheduleInterviewModal, setShowScheduleInterviewModal] = useState(false);
  const [showSendReminderModal, setShowSendReminderModal] = useState(false);
  const [interviewDate, setInterviewDate] = useState('');
  const [interviewTime, setInterviewTime] = useState('');
  const [interviewMode, setInterviewMode] = useState('online');
  const [reminderMessage, setReminderMessage] = useState('');


  const handleBulkAction = async (action) => {
    if (selectedApplicants.length === 0) {
      setError('Please select at least one applicant');
      return;
    }

    try {
      if (action === 'delete') {
        // Delete selected applicants
        const deletePromises = selectedApplicants.map(id =>
          jobApplicationService.deleteApplication(id)
        );
        await Promise.all(deletePromises);
        await loadApplications();
        setSelectedApplicants([]);
      } else if (action === 'assign-hr') {
        // Assign HR feature has been moved to HR Dashboard
        setError('Assign HR feature is now available in HR Dashboard. Please navigate to HR Dashboard to assign applicants to HR personnel.');
        return;
      } else if (action === 'schedule-interview') {
        setShowScheduleInterviewModal(true);
      } else if (action === 'send-reminder') {
        setShowSendReminderModal(true);
      } else if (action === 'updateStatus') {
        // This would need a status parameter - for now just log
        console.log('Bulk status update not yet implemented');
      }
    } catch (err) {
      console.error('Error performing bulk action:', err);
      setError(err.message || 'Failed to perform bulk action');
    }
  };


  const handleBulkScheduleInterview = async () => {
    if (!interviewDate) {
      setError('Please select an interview date');
      return;
    }

    try {
      const interviewDateTime = interviewTime
        ? `${interviewDate} ${interviewTime}:00`
        : `${interviewDate} 10:00:00`;

      const updatePromises = selectedApplicants.map(id =>
        jobApplicationService.scheduleInterview({
          job_application_id: id,
          interview_date: interviewDateTime,
          mode: interviewMode,
          stage: 'internal'
        })
      );

      await Promise.all(updatePromises);
      await loadApplications();
      setSelectedApplicants([]);
      setShowScheduleInterviewModal(false);
      setInterviewDate('');
      setInterviewTime('');
      setInterviewMode('online');
      setError(null);
    } catch (err) {
      console.error('Error scheduling interview:', err);
      setError(err.message || 'Failed to schedule interview');
    }
  };

  const handleSendReminder = async () => {
    try {
      // Add interaction/note to each selected applicant
      const reminderPromises = selectedApplicants.map(id =>
        jobApplicationService.addInteraction(id, {
          type: 'reminder',
          notes: reminderMessage || 'Reminder sent for interview/appointment',
          interaction_date: new Date().toISOString()
        })
      );

      await Promise.all(reminderPromises);
      await loadApplications();
      setSelectedApplicants([]);
      setShowSendReminderModal(false);
      setReminderMessage('');
      setError(null);
    } catch (err) {
      console.error('Error sending reminder:', err);
      setError(err.message || 'Failed to send reminder');
    }
  };

  const handleFiltersChange = (newFilters) => {
    setFilters(newFilters);
  };

  const handleSelectionChange = (selectedIds) => {
    setSelectedApplicants(selectedIds);
  };

  const handleApplicantAdded = async (newApplicant) => {
    await loadApplications();
    setShowAddModal(false);
  };

  return (
    <div className="min-h-screen bg-background">
      <HeaderNavigation
        onSidebarToggle={() => setSidebarOpen(!sidebarOpen)}
        user={userProfile || user}
        notifications={[]}
      />

      <MainSidebar
        isOpen={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
      />

      <div className={`pt-16 transition-all duration-300 ${isCollapsed ? 'lg:ml-16' : 'lg:ml-64'}`}>
        <div className="p-6">
          <BreadcrumbNavigation />

          {/* Page Header */}
          <div className="border-b border-border bg-background -mx-6 px-6 mb-6">
            <div className="pt-6">
              <h1 className="text-3xl font-bold text-foreground mb-2">Applicant Tracking System</h1>
              <p className="text-muted-foreground mb-4">Manage recruitment lifecycle from application to offer letter generation</p>
            </div>
            <div className="flex space-x-1 overflow-x-auto pb-4">
              {tabs.map((tab) => (
                <Button
                  key={tab.id}
                  variant={activeTab === tab.id ? 'default' : 'ghost'}
                  onClick={() => handleTabChange(tab)}
                  className="flex items-center gap-2 whitespace-nowrap"
                >
                  <Icon name={tab.icon} size={16} />
                  {tab.label}
                </Button>
              ))}
            </div>
          </div>

          {activeTab === 'dashboard' && (
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between mb-8">
              <div>
                <h2 className="text-xl font-semibold text-foreground">ATS Dashboard</h2>
                <p className="text-sm text-muted-foreground">Track and manage your applicant pipeline</p>
              </div>
              <div className="flex items-center gap-3 mt-4 lg:mt-0">
                <Button variant="outline" iconName="Download" iconPosition="left">
                  Export Data
                </Button>
                <Button
                  variant="outline"
                  iconName="Upload"
                  iconPosition="left"
                  onClick={() => setShowImportModal(true)}
                >
                  Import Applicants
                </Button>
                <Button
                  iconName="Plus"
                  iconPosition="left"
                  onClick={() => setShowAddModal(true)}
                >
                  Add Applicant
                </Button>
              </div>
            </div>
          )}

          {/* Error Message */}
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
              <div className="flex items-center gap-3">
                <div className="text-red-600">⚠️</div>
                <div>
                  <p className="text-red-800 font-medium">Error loading data</p>
                  <p className="text-red-600 text-sm">{error}</p>
                  <button
                    onClick={loadApplications}
                    className="text-red-700 underline text-sm mt-1"
                  >
                    Try again
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Loading State */}
          {loading && (
            <div className="flex items-center justify-center py-12">
              <div className="text-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-2"></div>
                <p className="text-muted-foreground text-sm">Loading applicants...</p>
              </div>
            </div>
          )}

          {/* Main Content */}
          {activeTab === 'dashboard' ? (
            !loading && (
              <div className="space-y-6">
                {/* Recruitment Funnel - Horizontal at Top */}
                <RecruitmentFunnel funnelData={funnelData} />

                {/* Filters and Table Section */}
                <div className="space-y-6">
                  {/* Filters */}
                  <ApplicantFilters
                    filters={filters}
                    onFiltersChange={handleFiltersChange}
                    resultCount={filteredApplicants.length}
                    positions={uniquePositions}
                  />

                  {/* Applicants Table */}
                  {filteredApplicants.length > 0 ? (
                    <>
                      <ApplicantTable
                        applicants={filteredApplicants}
                        onApplicantClick={handleApplicantClick}
                        onBulkAction={handleBulkAction}
                        selectedApplicants={selectedApplicants}
                        onSelectionChange={handleSelectionChange}
                        onCallApplicant={handleCallApplicant}
                        onScheduleInterview={handleScheduleInterview}
                        onMoreActions={handleMoreActions}
                        onDeleteApplicant={handleDeleteApplicant}
                      />

                      {/* Pagination */}
                      <div className="flex items-center justify-between">
                        <p className="text-sm text-muted-foreground">
                          Showing {filteredApplicants.length} of {applicants.length} applicants
                        </p>
                        <div className="flex items-center gap-2">
                          <Button variant="outline" size="sm" iconName="ChevronLeft">
                            Previous
                          </Button>
                          <Button variant="outline" size="sm">
                            1
                          </Button>
                          <Button variant="outline" size="sm" iconName="ChevronRight">
                            Next
                          </Button>
                        </div>
                      </div>
                    </>
                  ) : (
                    <div className="text-center py-12">
                      <p className="text-muted-foreground">No applicants found</p>
                      <Button
                        variant="outline"
                        className="mt-4"
                        onClick={() => setShowAddModal(true)}
                      >
                        Add First Applicant
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            )
          ) : activeTab === 'duplicates' ? (
            <DuplicateApplicationsView
              onApplicantClick={handleApplicantClick}
              onDataChange={loadApplications}
            />
          ) : activeTab === 'offer-letters' ? (
            <OfferLetters />
          ) : activeTab === 'probation-letters' ? (
            <ProbationLetters />
          ) : null}
        </div>
      </div>

      {/* Applicant Detail Modal */}
      <ApplicantDetailModal
        applicant={selectedApplicant}
        isOpen={!!selectedApplicant}
        onClose={handleCloseModal}
        initialTab={modalInitialTab}
        onStageChange={loadApplications}
      />

      {/* Add Applicant Modal */}
      <AddApplicantModal
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
        onApplicantAdded={handleApplicantAdded}
      />

      {/* Schedule Interview Modal */}
      {showScheduleInterviewModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => { setShowScheduleInterviewModal(false); setInterviewDate(''); setInterviewTime(''); setInterviewMode('online'); }}>
          <div className="bg-background rounded-lg p-6 max-w-md w-full mx-4" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-foreground">Schedule Interview</h3>
              <Button variant="ghost" size="icon" onClick={() => { setShowScheduleInterviewModal(false); setInterviewDate(''); setInterviewTime(''); setInterviewMode('online'); }}>
                <Icon name="X" size={20} />
              </Button>
            </div>
            <p className="text-sm text-muted-foreground mb-4">
              Schedule interview for {selectedApplicants.length} selected applicant(s)
            </p>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">Interview Date *</label>
                <Input
                  type="date"
                  value={interviewDate}
                  onChange={(e) => setInterviewDate(e.target.value)}
                  min={new Date().toISOString().split('T')[0]}
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">Interview Time</label>
                <Input
                  type="time"
                  value={interviewTime}
                  onChange={(e) => setInterviewTime(e.target.value)}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">Interview Mode</label>
                <select
                  value={interviewMode}
                  onChange={(e) => setInterviewMode(e.target.value)}
                  className="w-full px-3 py-2 border border-border rounded-md bg-background text-foreground"
                >
                  <option value="online">Online</option>
                  <option value="offline">Offline</option>
                  <option value="phone">Phone</option>
                </select>
              </div>
            </div>
            {error && (
              <div className="mt-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm">
                {error}
              </div>
            )}
            <div className="flex gap-3 justify-end mt-6">
              <Button variant="outline" onClick={() => { setShowScheduleInterviewModal(false); setInterviewDate(''); setInterviewTime(''); setInterviewMode('online'); setError(null); }}>
                Cancel
              </Button>
              <Button onClick={handleBulkScheduleInterview} disabled={!interviewDate}>
                Schedule Interview
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Send Reminder Modal */}
      {showSendReminderModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => { setShowSendReminderModal(false); setReminderMessage(''); }}>
          <div className="bg-background rounded-lg p-6 max-w-md w-full mx-4" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-foreground">Send Reminder</h3>
              <Button variant="ghost" size="icon" onClick={() => { setShowSendReminderModal(false); setReminderMessage(''); }}>
                <Icon name="X" size={20} />
              </Button>
            </div>
            <p className="text-sm text-muted-foreground mb-4">
              Send reminder to {selectedApplicants.length} selected applicant(s)
            </p>
            <div className="mb-4">
              <label className="block text-sm font-medium text-foreground mb-2">Reminder Message</label>
              <textarea
                value={reminderMessage}
                onChange={(e) => setReminderMessage(e.target.value)}
                className="w-full px-3 py-2 border border-border rounded-md bg-background text-foreground"
                rows="4"
                placeholder="Enter reminder message (optional - default message will be used if empty)"
              />
            </div>
            {error && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm">
                {error}
              </div>
            )}
            <div className="flex gap-3 justify-end">
              <Button variant="outline" onClick={() => { setShowSendReminderModal(false); setReminderMessage(''); setError(null); }}>
                Cancel
              </Button>
              <Button onClick={handleSendReminder}>
                Send Reminder
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Import Applicants Modal */}
      <ImportApplicantsModal
        isOpen={showImportModal}
        onClose={() => setShowImportModal(false)}
        onImportSuccess={() => {
          loadApplications();
        }}
      />
    </div>
  );
};

export default ApplicantTrackingSystem;